import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Film, Link2, Trash2, Search } from "lucide-react";
import { api } from "../api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOLD_TOAST = { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" };

export const AdminClips = () => {
  const [dishes, setDishes] = useState([]);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState({});
  const [urlFor, setUrlFor] = useState(null);
  const [urlVal, setUrlVal] = useState("");

  const load = () => api.get("/admin/clips").then((r) => setDishes(r.data.dishes)).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadFile = async (slug, file) => {
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) return toast.error("Keep the clip under 60 MB");
    const CHUNK = 4 * 1024 * 1024;
    const total = Math.ceil(file.size / CHUNK);
    const uploadId = [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, "0")).join("");
    setUploading((u) => ({ ...u, [slug]: 0 }));
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", i);
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "part.bin");
        await api.post(`/admin/story-video/${slug}/chunk`, fd);
        setUploading((u) => ({ ...u, [slug]: Math.round(((i + 1) / (total + 1)) * 100) }));
      }
      await api.post(`/admin/story-video/${slug}/finish`, { upload_id: uploadId, total_chunks: total, filename: file.name });
      toast.success("Clip is live on that dish's page", { style: GOLD_TOAST });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed — please try again");
    }
    setUploading((u) => { const n = { ...u }; delete n[slug]; return n; });
  };

  const saveUrl = async (slug) => {
    try {
      await api.post(`/admin/story-video/${slug}/set-url`, { url: urlVal });
      toast.success("Video link saved", { style: GOLD_TOAST });
      setUrlFor(null);
      setUrlVal("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that link");
    }
  };

  const remove = async (slug) => {
    try {
      await api.delete(`/admin/story-video/${slug}`);
      toast.success("Clip removed", { style: GOLD_TOAST });
      load();
    } catch {
      toast.error("Could not remove the clip");
    }
  };

  const list = dishes.filter((d) => d.name.toLowerCase().includes(q.toLowerCase()));
  const withClips = list.filter((d) => d.has_clip).length;

  return (
    <div data-testid="admin-clips" className="space-y-3">
      <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold">DISH VIDEOS — {withClips} OF {dishes.length} DISHES HAVE A CLIP</p>
      <p className="text-[11px] text-white/45 leading-relaxed">
        Upload a short muted cooking clip (mp4/webm/mov, under 60 MB) or paste a hosted video link for ANY dish.
        The clip loops in the dish's story section, shows a CLIP badge on the menu, previews on hover, and the newest one opens the attract screen.
      </p>
      <div className="flex items-center gap-2 rounded-xl bg-[#1A1A1A] border border-white/10 px-3">
        <Search size={14} className="text-white/40 shrink-0" />
        <input data-testid="clips-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dishes…"
          className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-white/30" />
      </div>
      <div className="rounded-xl bg-[#121212] border border-white/10 divide-y divide-white/5">
        {list.map((d) => (
          <div key={d.slug} data-testid={`clip-row-${d.slug}`} className="p-3">
            <div className="flex items-center gap-3">
              <img src={d.image} alt={d.name} className="w-10 h-10 rounded-lg object-cover shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-bold leading-snug">{d.name}</span>
                {d.has_clip ? (
                  <span className="text-[9px] font-black tracking-widest text-[#6FBF73]">
                    <Film size={9} className="inline mr-1" />
                    {d.external ? "LINKED VIDEO" : `UPLOADED${d.size ? ` · ${(d.size / 1048576).toFixed(1)} MB` : ""}`}
                  </span>
                ) : (
                  <span className="text-[9px] tracking-widest text-white/30">NO CLIP</span>
                )}
              </span>
              <span className="flex items-center gap-1.5 shrink-0">
                <label className={`cursor-pointer text-[8px] font-black tracking-widest rounded-full px-3 py-1.5 bg-[#D4AF37] text-black ${uploading[d.slug] != null ? "opacity-60" : ""}`}>
                  {uploading[d.slug] != null ? `${uploading[d.slug]}%` : d.has_clip ? "REPLACE" : "UPLOAD"}
                  <input data-testid={`clip-upload-${d.slug}`} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden"
                    disabled={uploading[d.slug] != null} onChange={(e) => { uploadFile(d.slug, e.target.files[0]); e.target.value = ""; }} />
                </label>
                <button data-testid={`clip-url-btn-${d.slug}`} aria-label={`Set video link for ${d.name}`}
                  onClick={() => { setUrlFor(urlFor === d.slug ? null : d.slug); setUrlVal(d.external ? d.source : ""); }}
                  className="w-7 h-7 rounded-full border border-white/20 flex items-center justify-center text-white/60">
                  <Link2 size={11} />
                </button>
                {d.has_clip && (
                  <button data-testid={`clip-remove-${d.slug}`} aria-label={`Remove clip from ${d.name}`} onClick={() => remove(d.slug)}
                    className="w-7 h-7 rounded-full border border-[#E25822]/50 flex items-center justify-center text-[#E25822]">
                    <Trash2 size={11} />
                  </button>
                )}
              </span>
            </div>
            {urlFor === d.slug && (
              <div className="flex gap-2 mt-2">
                <input data-testid={`clip-url-input-${d.slug}`} value={urlVal} onChange={(e) => setUrlVal(e.target.value)}
                  placeholder="https://…/clip.mp4"
                  className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/10 rounded-full px-3 py-2 text-xs outline-none" />
                <button data-testid={`clip-url-save-${d.slug}`} onClick={() => saveUrl(d.slug)}
                  className="shrink-0 text-[9px] font-black tracking-widest bg-[#D4AF37] text-black rounded-full px-4 py-2">SAVE</button>
              </div>
            )}
            {d.has_clip && !d.external && urlFor === d.slug && (
              <video src={`${BACKEND_URL}/api/experience/story-video/${d.slug}`} poster={d.image} controls muted playsInline
                className="w-full max-h-40 rounded-xl mt-2 border border-white/10 bg-black" preload="none" />
            )}
          </div>
        ))}
        {list.length === 0 && <p className="p-4 text-xs text-white/40">No dishes match that search.</p>}
      </div>
    </div>
  );
};
